import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../ui/button";

const COLORS = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];
const ROUNDS = 10;

export default function ReactionTimer({ onComplete }) {
  const [state, setState] = useState("idle"); // idle, waiting, ready, clicked, finished
  const [round, setRound] = useState(0);
  const [times, setTimes] = useState([]);
  const [currentColor, setCurrentColor] = useState("#ef4444");
  const [tooEarly, setTooEarly] = useState(false);
  const readyAt = useRef(null);
  const timerRef = useRef(null);

  const startRound = useCallback(() => {
    setState("waiting");
    setTooEarly(false);
    const delay = 1500 + Math.random() * 3000;
    timerRef.current = setTimeout(() => {
      setCurrentColor(COLORS[Math.floor(Math.random() * COLORS.length)]);
      readyAt.current = Date.now();
      setState("ready");
    }, delay);
  }, []);

  const handleTap = () => {
    if (state === "waiting") {
      clearTimeout(timerRef.current);
      setTooEarly(true);
      setState("idle");
    } else if (state === "ready") {
      const reaction = Date.now() - readyAt.current;
      const newTimes = [...times, reaction];
      setTimes(newTimes);
      const nextRound = round + 1;
      setRound(nextRound);
      if (nextRound >= ROUNDS) {
        setState("finished");
      } else {
        setState("clicked");
        setTimeout(startRound, 800);
      }
    }
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const avgTime = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0;
  const bestTime = times.length ? Math.min(...times) : 0;
  const score = times.length ? Math.max(0, Math.round(1000 - avgTime * 2)) : 0;

  if (state === "finished") {
    return (
      <div data-testid="reaction-result" className="text-center py-8 space-y-6">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mx-auto shadow-xl">
          <span className="text-3xl font-bold text-white">{score}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Focus Score</h3>
          <p className="text-gray-500">Avg: {avgTime}ms | Best: {bestTime}ms</p>
          <div className="flex justify-center gap-1 mt-3">
            {times.map((t, i) => (
              <div key={i} className={`w-8 h-8 rounded text-xs font-bold flex items-center justify-center ${t < 300 ? 'bg-emerald-100 text-emerald-700' : t < 500 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                {t}
              </div>
            ))}
          </div>
        </div>
        <Button data-testid="save-reaction-score" onClick={() => onComplete(score, ROUNDS)} className="bg-emerald-600 hover:bg-emerald-700">
          Save Score & Return
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="reaction-timer-game" className="flex flex-col items-center py-6 space-y-6">
      {state === "idle" && !tooEarly && (
        <>
          <p className="text-sm text-gray-600 text-center max-w-md">Tap the circle as fast as you can when it changes color. Test your reaction speed across {ROUNDS} rounds.</p>
          <Button data-testid="start-reaction" onClick={startRound} className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-3">Start Game</Button>
        </>
      )}

      {tooEarly && (
        <div className="text-center space-y-3">
          <p className="text-red-600 font-bold">Too early! Wait for the color change.</p>
          <Button onClick={startRound} variant="outline">Try Again</Button>
        </div>
      )}

      {(state === "waiting" || state === "ready") && (
        <>
          <p className="text-xs text-gray-500">Round {round + 1} of {ROUNDS}</p>
          <button
            data-testid="reaction-target"
            onClick={handleTap}
            className="w-48 h-48 rounded-full transition-all duration-100 flex items-center justify-center shadow-2xl cursor-pointer"
            style={{ backgroundColor: state === "ready" ? currentColor : "#d1d5db" }}
          >
            <span className="text-white text-lg font-bold">
              {state === "waiting" ? "Wait..." : "TAP!"}
            </span>
          </button>
        </>
      )}

      {state === "clicked" && (
        <div className="text-center">
          <p className="text-2xl font-bold text-emerald-600">{times[times.length - 1]}ms</p>
          <p className="text-xs text-gray-400">Get ready for next round...</p>
        </div>
      )}
    </div>
  );
}
